import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPixelatedPass } from 'three/examples/jsm/postprocessing/RenderPixelatedPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

const DEFAULTS = {
  enabled: true,
  pixelEnabled: true,
  dualToneEnabled: true,
  bloomEnabled: true,
  pixelSize: 4,
  normalEdgeStrength: 0.3,
  depthEdgeStrength: 0.4,
  shadowColor: 0x08080a,
  highlightColor: 0xff2d6b,
  threshold: 0.4,
  softness: 0.11,
  contrast: 1.3,
  saturationMix: 0.92,
  bloomStrength: 0.8,
  bloomRadius: 0.6,
  bloomThreshold: 0.4,
};

export const ROOM_PALETTES = [
  { shadowColor: 0x080808, highlightColor: 0xff2d6b },
  { shadowColor: 0x080808, highlightColor: 0xff2d6b },
  { shadowColor: 0x080808, highlightColor: 0xff2d6b },
  { shadowColor: 0x080808, highlightColor: 0xff2d6b },
  { shadowColor: 0x080808, highlightColor: 0xff2d6b },
  { shadowColor: 0x080808, highlightColor: 0xff2d6b },
];

export function getRoomPaletteIndex(roomNumber) {
  const room = Math.max(1, Math.min(10, Number(roomNumber) || 1));
  if (room <= 2) return 0;
  if (room <= 4) return 1;
  if (room === 5) return 2;
  if (room <= 7) return 3;
  if (room <= 9) return 4;
  return 5;
}

const DualToneShader = {
  uniforms: {
    tDiffuse: { value: null },
    shadowColor: { value: new THREE.Vector3(0.03, 0.03, 0.04) },
    highlightColor: { value: new THREE.Vector3(0.88, 0.22, 0.36) },
    threshold: { value: 3 },
    softness: { value: 0.11 },
    contrast: { value: 3},
    saturationMix: { value: 0.92 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform vec3 shadowColor;
    uniform vec3 highlightColor;
    uniform float threshold;
    uniform float softness;
    uniform float contrast;
    uniform float saturationMix;
    varying vec2 vUv;

    float luma(vec3 c) {
      return dot(c, vec3(0.2126, 0.7152, 0.0722));
    }

    void main() {
      vec4 src = texture2D(tDiffuse, vUv);
      float lum = luma(src.rgb);

      float c = max(0.001, contrast);
      float centered = (lum - 0.5) * c + 0.5;
      float lit = clamp(centered, 0.0, 1.0);

      float s = max(0.001, softness);
      float t = smoothstep(threshold - s, threshold + s, lit);
      vec3 duo = mix(shadowColor, highlightColor, t);

      vec3 gray = vec3(lit);
      vec3 finalColor = mix(gray, duo, clamp(saturationMix, 0.0, 1.0));
      gl_FragColor = vec4(finalColor, src.a);
    }
  `,
};

function writeColor(vec3, input) {
  const c = new THREE.Color(input);
  vec3.set(c.r, c.g, c.b);
}

export function createDualTonePostprocess(renderer, scene, camera, options = {}) {
  const state = { ...DEFAULTS, ...options };
  const activeColors = {
    shadowColor: new THREE.Color(state.shadowColor),
    highlightColor: new THREE.Color(state.highlightColor),
  };
  const colorTween = {
    active: false,
    elapsed: 0,
    duration: 1.5,
    fromShadow: activeColors.shadowColor.clone(),
    fromHighlight: activeColors.highlightColor.clone(),
    toShadow: activeColors.shadowColor.clone(),
    toHighlight: activeColors.highlightColor.clone(),
  };

  const composer = new EffectComposer(renderer);
  composer.setPixelRatio(renderer.getPixelRatio());
  composer.setSize(window.innerWidth, window.innerHeight);

  const renderPixelatedPass = new RenderPixelatedPass(state.pixelSize, scene, camera);
  composer.addPass(renderPixelatedPass);
  const dualTonePass = new ShaderPass(DualToneShader);
  composer.addPass(dualTonePass);
  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    state.bloomStrength,
    state.bloomRadius,
    state.bloomThreshold,
  );
  composer.addPass(bloomPass);
  composer.addPass(new OutputPass());

  const applyState = () => {
    renderPixelatedPass.enabled = Boolean(state.enabled && state.pixelEnabled);
    renderPixelatedPass.setPixelSize(Math.max(1, Math.floor(state.pixelSize)));
    renderPixelatedPass.normalEdgeStrength = state.normalEdgeStrength;
    renderPixelatedPass.depthEdgeStrength = state.depthEdgeStrength;

    dualTonePass.enabled = Boolean(state.enabled && state.dualToneEnabled);
    activeColors.shadowColor.set(state.shadowColor);
    activeColors.highlightColor.set(state.highlightColor);
    writeColor(dualTonePass.uniforms.shadowColor.value, activeColors.shadowColor);
    writeColor(dualTonePass.uniforms.highlightColor.value, activeColors.highlightColor);
    dualTonePass.uniforms.threshold.value = state.threshold;
    dualTonePass.uniforms.softness.value = state.softness;
    dualTonePass.uniforms.contrast.value = state.contrast;
    dualTonePass.uniforms.saturationMix.value = state.saturationMix;

    bloomPass.enabled = Boolean(state.enabled && state.bloomEnabled);
    bloomPass.strength = state.bloomStrength;
    bloomPass.radius = state.bloomRadius;
    bloomPass.threshold = state.bloomThreshold;
  };
  applyState();

  const updateColorTransition = (dt) => {
    if (!colorTween.active) return;
    const step = Number.isFinite(dt) && dt > 0 ? dt : 0;
    colorTween.elapsed += step;
    const rawT = colorTween.duration > 0 ? Math.min(1, colorTween.elapsed / colorTween.duration) : 1;
    const easedT = rawT * rawT * (3 - 2 * rawT); // smoothstep easing
    activeColors.shadowColor.copy(colorTween.fromShadow).lerp(colorTween.toShadow, easedT);
    activeColors.highlightColor.copy(colorTween.fromHighlight).lerp(colorTween.toHighlight, easedT);
    writeColor(dualTonePass.uniforms.shadowColor.value, activeColors.shadowColor);
    writeColor(dualTonePass.uniforms.highlightColor.value, activeColors.highlightColor);
    state.shadowColor = activeColors.shadowColor.getHex();
    state.highlightColor = activeColors.highlightColor.getHex();
    if (rawT >= 1) colorTween.active = false;
  };

  return {
    render(dt) {
      updateColorTransition(dt);
      if (state.enabled) {
        composer.render(dt);
      } else {
        renderer.render(scene, camera);
      }
    },
    resize(width, height) {
      composer.setSize(width, height);
    },
    setOptions(next = {}) {
      Object.assign(state, next);
      applyState();
    },
    transitionToRoom(roomNumber, duration = 1.5) {
      const palette = ROOM_PALETTES[getRoomPaletteIndex(roomNumber)] || ROOM_PALETTES[0];
      const targetShadow = new THREE.Color(palette.shadowColor);
      const targetHighlight = new THREE.Color(palette.highlightColor);
      const hasDuration = Number.isFinite(duration) && duration > 0;
      if (!hasDuration) {
        activeColors.shadowColor.copy(targetShadow);
        activeColors.highlightColor.copy(targetHighlight);
        state.shadowColor = targetShadow.getHex();
        state.highlightColor = targetHighlight.getHex();
        writeColor(dualTonePass.uniforms.shadowColor.value, activeColors.shadowColor);
        writeColor(dualTonePass.uniforms.highlightColor.value, activeColors.highlightColor);
        colorTween.active = false;
        return;
      }
      colorTween.fromShadow.copy(activeColors.shadowColor);
      colorTween.fromHighlight.copy(activeColors.highlightColor);
      colorTween.toShadow.copy(targetShadow);
      colorTween.toHighlight.copy(targetHighlight);
      colorTween.elapsed = 0;
      colorTween.duration = Math.max(0.001, duration);
      colorTween.active = true;
    },
    // Backward-compatible no-op APIs retained for existing debug scripts.
    setLutTexture3D() {},
    async loadLutImage() { return null; },
    async loadLutCube() { return null; },
    async loadLut3dl() { return null; },
    clearLut() {
      return null;
    },
    getOptions() {
      return { ...state };
    },
  };
}

